import { ClusterDataService } from '@qdrant/qdrant-cloud-public-api/ui/v1/clusterdata';
import { listCollections } from '@qdrant/qdrant-cloud-public-api/ui/v1/clusterdata/connect-query';
import { type DefaultError, type UseQueryOptions } from '@tanstack/react-query';
import { isUnavailableError, queryRetryHandler } from '@/api/errors';
import { transport } from '@/api/transport';
import { queryOptions, createServiceQueries } from '@/connect-query';

/**
 * `ListCollections` proxies to the cluster itself, so it has one transient failure mode of its own:
 * the API answers `UNAVAILABLE` (qc-error-code `E1129`) while the cluster's public endpoint is not
 * routable yet. The gateway programs the route asynchronously after the operator creates *or
 * resumes* a cluster, so a cluster already reported `HEALTHY` can still refuse connections for a
 * short while. That status is authoritative and carries no dependency on the cluster's age, which
 * makes it the right thing to key retries on — a months-old cluster un-suspended a minute ago hits
 * exactly the same window as a freshly created one.
 *
 * Nothing else is treated as transient: `RESOURCE_EXHAUSTED` (`E1130`) is the cluster asking us to
 * back off, and `INTERNAL` (`E1120`) now means a genuine failure that must surface and be reported.
 *
 * One retry only: the API already retries the cluster three times internally before answering
 * `UNAVAILABLE` ("max retries exhausted: failed after 3 attempts"), so a long client-side ladder
 * mostly multiplies upstream attempts. Recovery over the longer route-programming window is the
 * caller's job — the collections banner polls for it.
 */
const LIST_COLLECTIONS_UNAVAILABLE_RETRIES = 1;
const LIST_COLLECTIONS_RETRY_DELAY_MILLISECONDS = 2_000;

/**
 * Retry and error-reporting policy baked into `listCollectionsQuery` below, so every consumer gets
 * it by inheriting the query options — the transient window is a property of the RPC, not of any one
 * call site, and an opt-in was one forgotten spread away from a call site reporting phantom errors.
 *
 * Overriding is still possible, but it has to be deliberate: a consumer that spreads the query
 * options and then sets its own `retry`, `retryDelay` or `meta` wins, since the later key replaces
 * this one wholesale. That also means a consumer needing its own `meta` must merge `sentryFilter`
 * back in by hand.
 *
 * Deliberately scoped to this query instead of keying the global handler (or `isRetryableError`) on
 * `UNAVAILABLE`: that status means "not ready yet, retry" for a cluster endpoint we proxy to, but
 * for the control-plane API itself it is an outage worth alerting on. Widening either would change
 * retries *and* reporting for every query in the app.
 */
const listCollectionsTransientErrorOptions = {
  retry: (failureCount: number, error: DefaultError) =>
    queryRetryHandler(failureCount, error) ||
    (isUnavailableError(error) && failureCount < LIST_COLLECTIONS_UNAVAILABLE_RETRIES),
  retryDelay: LIST_COLLECTIONS_RETRY_DELAY_MILLISECONDS,
  // A cluster endpoint that isn't routable yet is a known, self-clearing state of a healthy cluster:
  // there is no action anyone can take on the report, so don't store it. Everything else — including
  // `INTERNAL` — is reported as usual.
  meta: { sentryFilter: (error: unknown) => !isUnavailableError(error) },
} satisfies Pick<UseQueryOptions, 'retry' | 'retryDelay' | 'meta'>;

export const clusterDataService = createServiceQueries(ClusterDataService, {
  listCollectionsQuery: (params) => ({
    ...queryOptions(listCollections, params, { transport, select: (data) => data.items }),
    ...listCollectionsTransientErrorOptions,
  }),
});
